import { LOWERCASED_UUID_PATTERN } from "./deployment-config.js";
import { refineTierCapPoolTotals } from "./desired-state.schemas.js";
import { APP_POOL_MAX_NODES_MAX, APP_POOL_MAX_NODES_MIN, appPoolNameSchema, appPoolTierKeySchema } from "@zion/sdk-contracts";
import { z } from "zod";
//#region src/dp-pool-tier-caps.ts
/**
* The tier cap override routes: a platform administrator lowers or raises the node cap of one tier
* of one tenant pool on one data plane (2026-10-24-pool-machine-generations D-11, D-45, R-006).
*
* Two services serve the same three verbs. provisioning-svc owns the override table in the
* `provisioning` schema and serves the paths under `/v1/data-planes` (WS-1c). site-admin-api proxies
* them under `/v1/admin` (WS-1e). The console and the `zion-ops` verb call the proxy (S2, WS-2g and
* WS-2h). Both prefixes are reachable from outside the cluster: the CP gateway's `cp-httproute`
* sends every `/v1/data-planes` path to provisioning-svc. So the control on each service is its own
* `@RequirePermission`: {@link DP_POOL_TIER_CAPS_READ_PERMISSION} on `GET`,
* {@link DP_POOL_TIER_CAPS_WRITE_PERMISSION} on `PUT` and `DELETE`. Both services bind the schemas
* below with `@Param({ schema })` and `@Body({ schema })` under the global
* `ZionStrictValidationPipe`, so an unknown key at any depth is a 400 before a handler runs. The
* `DELETE` verb must bind {@link deleteDpPoolTierCapQuerySchema} with `@Query({ schema })` as well:
* site-admin-api forwards the query (WS-3h, stage S3b), and provisioning-svc compares it (WS-3f,
* stage S3d).
*
* The R-030 checks land in provisioning-svc in stage S3d (WS-3f): the `expectedMaxNodes` compare of
* `PUT` and `DELETE`, and the declared-tier 404 of `PUT`. A provisioning-svc from before S3d
* ignores `expectedMaxNodes`: it writes and removes with no compare.
*
* The verbs:
* - `GET` {@link DP_POOL_TIER_CAPS_PATH}: every override row of the data plane. Both services answer
*   {@link dpPoolTierCapsResponseSchema}; site-admin-api forwards provisioning-svc's answer as it is.
*   A data plane with no override answers `caps: []`. An unknown data plane id is a 404.
* - `PUT` {@link DP_POOL_TIER_CAP_PATH}: create or replace the row for one `(orgId, appId, pool,
*   tier)`, body {@link putDpPoolTierCapRequestSchema}, answer 200 with {@link dpPoolTierCapSchema}.
*   PUT is replace: the body is the whole row value. The server answers 404, and writes nothing,
*   when the data plane is unknown, when `release.apps` holds no app with `appId`, or when the org
*   is not placed on the data plane. The org check is the relationship assertion: the path names
*   two ids that a caller picks apart, and a row for an org that the data plane does not host is a
*   setting no operator can apply. The server also answers 404, and writes nothing, when no
*   released version of the app declares a tier `tier` in the pool `pool` (R-030 item 2, R-045).
*   A version declares that tier when one of its tiers of `pool` has the `appPoolTierKey` `tier`.
*   A released version is any
*   `release.app_compositions` row of the app (R-047).
*   The server answers 409, and writes nothing, when the body carries `expectedMaxNodes` and the
*   stored row differs from it (R-030 item 1): a number with no stored row or with another stored
*   cap, or `null` with a stored row. The compare and the write run in one transaction, so two
*   administrators who read one cap cannot both replace it. A body with no `expectedMaxNodes`
*   writes with no compare.
*   The server answers 409, and writes nothing, when the new row would make the override rows of
*   the same `(id, orgId, appId, pool)` on the tier's cloud total more than
*   `APP_POOL_MAX_NODES_MAX` (100) nodes (D-25, DEF-1). The cloud is the first part of the tier key,
*   and the total counts the new `maxNodes` in place of the row it replaces. A pool's node maximum
*   on a data plane is the sum of its tier caps on that cloud, and the publish rule
*   `pool-tiers-max-nodes-exceeded` holds the declared caps to the same ceiling. So two overrides of
*   60 on two tiers of one pool are a 409 at the second `PUT`. The answer below holds the same
*   total, so a service that skips the check answers a body its own contract refuses.
*   The server counts the override rows alone, because the declared caps of a tier live in the app
*   version the org runs, and the CP does not resolve that version for the total (R-009); the
*   declared-tier check above asks only whether some released version names the key. The operator holds
*   the total of the merge (`DesiredStateEnvelope.appPoolTierCaps` in `desired-state.ts`). Each
*   tier with a row takes the row's cap, and each tier with no row keeps its declared cap. When
*   that merged total passes 100, the operator applies no row of the pool for that cycle and says
*   so in the `PoolTiers` status (R-009). Take a pool declared at 60 and 40 with an override of 70
*   on the first tier: the `PUT` answers 200, and the operator holds the row back while the merged
*   total is 110. An override of 50 merges to 90, and the operator applies it.
*   An override applies to the org's own node group of an out-of-mesh pool. An in-mesh pool takes
*   no override (R-009): its node group carries `orgId: null` and serves every org of the app. The
*   server does not read whether the pool is in-mesh. It stores such a row, and no operator applies
*   it.
* - `DELETE` {@link DP_POOL_TIER_CAP_PATH}: remove the row. The answer is 204 with no body when a
*   row went, and 404 when no row matched (refuse over accept, DEF-1). With no row, the tier runs at
*   the cap its pool declares. The verb takes no body. Its query {@link deleteDpPoolTierCapQuerySchema}
*   may carry `expectedMaxNodes`: the server then answers 409, and removes nothing, when the stored
*   cap differs from it (R-030 item 1). With no stored row it answers 404, as without the query.
*
* Each write of provisioning-svc writes one audit row (R-009): the action `data_plane.config_updated`
* on the data plane, the actor's verified JWT `sub`, and `details` with `setting`, `orgId`, `appId`,
* `pool`, `tier` and `changes.maxNodes` (the before and after caps). The audit write is best effort:
* a failed audit write does not fail the verb, as for every provisioning-svc setting write. The
* data plane id, the org id and the app id come from the path, and the caller's identity from the
* JWT; no route reads an id from the body.
*
* Who reads the rows: release-engine projects them into the desired-state envelope member
* `appPoolTierCaps` (`desired-state.ts`), keyed by the app's slug in place of its id (WS-1d). The
* operator applies them from stage S2 (WS-2b).
*
* Declared in `@zion/sdk-cp-dp-contracts` and not in `@zion/sdk-contracts` (R-006): the row and the
* envelope member are one fact on two wires, and this package holds the envelope.
*/
/** provisioning-svc: every tier cap override of one data plane. The CP gateway routes the
*  `/v1/data-planes` prefix here, so the route's own `@RequirePermission` is the control. */
const DP_POOL_TIER_CAPS_PATH = "/v1/data-planes/:id/pool-tier-caps";
/** provisioning-svc: the override of one tier of one org's pool of one app on one data plane. */
const DP_POOL_TIER_CAP_PATH = `${DP_POOL_TIER_CAPS_PATH}/:orgId/:appId/:pool/:tier`;
/** site-admin-api: the browser-reachable proxy of {@link DP_POOL_TIER_CAPS_PATH}. */
const DP_POOL_TIER_CAPS_ADMIN_PATH = "/v1/admin/data-planes/:id/pool-tier-caps";
/** site-admin-api: the browser-reachable proxy of {@link DP_POOL_TIER_CAP_PATH}. */
const DP_POOL_TIER_CAP_ADMIN_PATH = `${DP_POOL_TIER_CAPS_ADMIN_PATH}/:orgId/:appId/:pool/:tier`;
/** The permission the `GET` verb requires on both services: the one every sibling data plane read
*  holds (the satellites read, the machine types read). */
const DP_POOL_TIER_CAPS_READ_PERMISSION = "cp:dp:read";
/** The permission `PUT` and `DELETE` require on both services (D-11, overview section 5). The
*  satellite writes and the enabled machine types PATCH require the same one. */
const DP_POOL_TIER_CAPS_WRITE_PERMISSION = "cp:dp:update";
/** A path id: an RFC 9562 UUID, lower-cased on parse. Postgres prints a `uuid` in lower case, and
*  the answer's invariants compare ids as exact strings. `.toLowerCase()` is a zod 4 overwrite, so
*  `createOpenApiDocument()` still renders a uuid string; a `.transform()` throws there. */
const pathIdSchema = z.string().uuid().toLowerCase();
/** A stored id in an answer: the lower-case UUID text Postgres prints, in the RFC 9562 grammar the
*  path ids hold. So every id an answer lists is an id a `PUT` or `DELETE` path accepts, and a
*  service whose stored id the path would refuse answers a body its own contract refuses (DEF-1).
*  `abort` stops the parse at a failed UUID check, so one bad id raises one issue. */
const storedIdSchema = z.string().uuid({ abort: true }).regex(LOWERCASED_UUID_PATTERN);
/** The bounds of an override's `maxNodes`: the bounds of a tier's own `maxNodes`, 1 to 100, so an
*  override names a cap the tier itself could declare. An override of 0 does not close a tier; the
*  cap moves, and the tier stays (DEF-1). One body names one tier, so this bound is per tier. The
*  pool total of 100 over every tier of a pool on one cloud holds in three places: the 409 of `PUT`,
*  the answer's refine {@link dpPoolTierCapsResponseSchema}, and the merge rule of the operator
*  (`DesiredStateEnvelope.appPoolTierCaps`). */
const overrideMaxNodesSchema = z.int().min(APP_POOL_MAX_NODES_MIN).max(APP_POOL_MAX_NODES_MAX);
/**
* Path parameters of the collection route: `id` is the data plane id. A malformed id is a 400; an
* unknown well-formed id is the service's 404. Parse lower-cases the id.
*/
const dpPoolTierCapsPathParamsSchema = z.object({ id: pathIdSchema }).strict();
/**
* Path parameters of the single-override routes:
* - `id`: the data plane id, a UUID, lower-cased on parse.
* - `orgId`: the CP org id (`platform.organizations.id`), a UUID, lower-cased on parse.
* - `appId`: the app id (`release.apps.id`), a UUID, lower-cased on parse. The row keys the app by
*   its id, as `provisioning.app_compute_overrides` does; release-engine joins `release.apps` for
*   the slug the envelope carries.
* - `pool`: the pool name, in `appPoolNameSchema`'s grammar.
* - `tier`: the tier key, in `appPoolTierKeySchema`'s grammar (`gcp.c4d.spot`), the output of
*   `appPoolTierKey` (R-003, E-5). The cap is not part of the key, so a changed cap is the same
*   tier.
*
* Any value outside its grammar, a missing parameter or an extra one is a `ZodError`, and the pipe
* answers 400. Pure.
*/
const dpPoolTierCapPathParamsSchema = z.object({
	id: pathIdSchema,
	orgId: pathIdSchema,
	appId: pathIdSchema,
	pool: appPoolNameSchema,
	tier: appPoolTierKeySchema
}).strict();
/**
* The `PUT` body.
*
* - `maxNodes`: the tier's node cap on this data plane for this org, an integer from
*   `APP_POOL_MAX_NODES_MIN` (1) to `APP_POOL_MAX_NODES_MAX` (100).
* - `expectedMaxNodes` (optional, R-030 item 1): the stored cap the caller read before the write.
*   An integer from 1 to 100 names a stored row with that cap; `null` names "no stored row". The
*   server answers 409 and writes nothing when the stored row differs. Absent, the server writes
*   with no compare, as before the member existed.
*
* Who reads `expectedMaxNodes`: provisioning-svc must pass it to the compare of the write (WS-3f,
* stage S3d). The site-admin-api proxy forwards the body as it is. A service that parses the body
* and drops the member writes with no compare, and a stale `PUT` then replaces a newer cap. Who
* sends it: the console (WS-3l) and `zion-ops` (WS-3m), from the cap they read.
*
* The key is the path's, so the body carries none. A fraction, a string, 0, 101 or any other key
* is a `ZodError` naming the member, and the pipe answers 400. Pure.
*/
const putDpPoolTierCapRequestSchema = z.object({
	maxNodes: overrideMaxNodesSchema,
	expectedMaxNodes: overrideMaxNodesSchema.nullable().optional()
}).strict();
/**
* The `DELETE` query (R-030 item 1, R-045): an optional `expectedMaxNodes`, the stored cap the
* caller read before the remove.
*
* A query value arrives as a string. The member first holds the string to
* {@link QUERY_CAP_TEXT_PATTERN}, the decimal text of an integer from 1 to 100, then converts it to
* a number: `'7'` gives `7`. The number must also be an integer from `APP_POOL_MAX_NODES_MIN` (1) to
* `APP_POOL_MAX_NODES_MAX` (100), so the input form and the output form state one range. Each of
* these is a `ZodError` naming the member, and the pipe answers 400:
* - `'0'`, `'101'`, `'1000'`, `'2.5'`, `'abc'` and the empty string;
* - a text that `Number()` reads but the grammar refuses: `'1e1'`, `'0x10'`, `'07'`, `'0100'`,
*   `'+7'`, `'7.0'`, `' 7'`, `'7 '` and `'7\n'`;
* - a value that is not a string: a number, a boolean, `null`, and the array that a repeated key
*   (`?expectedMaxNodes=7&expectedMaxNodes=8`) gives.
* Any other key is a `ZodError` as well.
*
* There is no `null` form: a `DELETE` with no stored row answers 404 already. An empty query
* parses to `{}`, and the server removes with no compare, as before the member existed.
*
* Who binds it: provisioning-svc (WS-3f) and the site-admin-api proxy (WS-3h), with
* `@Query({ schema })`. Who sends it: the console (WS-3l) and `zion-ops` (WS-3m) parse
* `{ expectedMaxNodes: '<n>' }` with it before they append `?expectedMaxNodes=<n>` to the path.
*
* The member is a zod pipe from a string to a number, with no `.transform()`. So
* `createOpenApiDocument()` still renders the schema: its output form is an integer from 1 to 100,
* and its input form is a string with the pattern `^(?:[1-9][0-9]?|100)$`. A consumer that renders
* the input form into an OpenAPI snapshot (the site-admin-api `openapi.json`) pins that pattern. The parsed type is
* `{ expectedMaxNodes?: number }`. `.strict()`. Pure.
*/
const deleteDpPoolTierCapQuerySchema = z.object({ expectedMaxNodes: z.string().regex(/^(?:[1-9][0-9]?|100)$/).pipe(z.coerce.number().int().min(APP_POOL_MAX_NODES_MIN).max(APP_POOL_MAX_NODES_MAX)).optional() }).strict();
/**
* One override row as both services serve it: the answer to a `PUT` and one member of the `GET`
* list.
*
* - `dataPlaneId`, `orgId`, `appId`: lower-case RFC 9562 UUIDs, the path's ids.
* - `pool`, `tier`: the path's pool name and tier key.
* - `maxNodes`: the stored cap, 1 to 100.
* - `updatedAt`: the ISO 8601 instant of the last write. A replacing `PUT` moves it.
*
* The row carries no actor: the audit row holds who wrote it. `.strict()`. A conforming object
* parses to itself; anything else is a `ZodError`. Pure.
*/
const dpPoolTierCapSchema = z.object({
	dataPlaneId: storedIdSchema,
	orgId: storedIdSchema,
	appId: storedIdSchema,
	pool: appPoolNameSchema,
	tier: appPoolTierKeySchema,
	maxNodes: overrideMaxNodesSchema,
	updatedAt: z.string().datetime()
}).strict();
/** The issue message for a `GET` answer member whose `dataPlaneId` is not the answer's. */
const FOREIGN_DATA_PLANE_MESSAGE = "every cap carries the answer's dataPlaneId";
/** The issue message for a `GET` answer member that sorts before the member above it. */
const CAP_ORDER_MESSAGE = "caps ascend by orgId, appId, pool and tier";
/** The issue message for a `GET` answer member whose key repeats the member above it. */
const CAP_REPEAT_MESSAGE = "each (orgId, appId, pool, tier) appears once";
/** The four key parts of one row, in sort order. */
function rowKey(row) {
	return [
		row.orgId,
		row.appId,
		row.pool,
		row.tier
	];
}
/** The code-unit order of two keys of equal length: negative, zero or positive. */
function compareKeys(a, b) {
	for (let i = 0; i < a.length; i += 1) {
		const left = a[i];
		const right = b[i];
		if (left !== right) return left < right ? -1 : 1;
	}
	return 0;
}
/**
* Raises the per-member issues of one `GET` answer:
* - a member whose `dataPlaneId` is not the answer's, at `['caps', i, 'dataPlaneId']`;
* - a member whose key equals the key of the member above it, at `['caps', i]`;
* - a member whose key sorts before the key of the member above it, at `['caps', i]`;
* - the member at which the caps of its pool on its cloud first total more than 100, at
*   `['caps', i]`.
*
* The members ascend, so a repeat always sits next to its first, and a check of each neighbour pair
* finds every repeat.
*/
function capIssues(answer, ctx) {
	answer.caps.forEach((row, i) => {
		if (row.dataPlaneId !== answer.dataPlaneId) ctx.addIssue({
			code: "custom",
			path: [
				"caps",
				i,
				"dataPlaneId"
			],
			message: FOREIGN_DATA_PLANE_MESSAGE
		});
		const above = answer.caps[i - 1];
		if (above === void 0) return;
		const order = compareKeys(rowKey(row), rowKey(above));
		if (order === 0) ctx.addIssue({
			code: "custom",
			path: ["caps", i],
			message: CAP_REPEAT_MESSAGE
		});
		if (order < 0) ctx.addIssue({
			code: "custom",
			path: ["caps", i],
			message: CAP_ORDER_MESSAGE
		});
	});
	refineTierCapPoolTotals(answer.caps, (row) => [row.orgId, row.appId], ["caps"], ctx);
}
/**
* The `GET` answer both services serve. `caps` is `[]` for a data plane with no override. The
* schema holds four invariants, so a service that breaks one answers a body its own contract
* refuses:
* - Every member carries the answer's `dataPlaneId`. A foreign member is an issue at
*   `['caps', i, 'dataPlaneId']`, "every cap carries the answer's dataPlaneId".
* - Each `(orgId, appId, pool, tier)` appears once (the table's primary key). A repeat is an issue
*   at `['caps', i]`, "each (orgId, appId, pool, tier) appears once".
* - Members ascend by `orgId`, then `appId`, then `pool`, then `tier`, in code-unit order, so two
*   reads of one state compare equal. A member that sorts before the one above it is an issue at
*   `['caps', i]`, "caps ascend by orgId, appId, pool and tier".
* - The caps of one `(orgId, appId, pool)` on one cloud total at most `APP_POOL_MAX_NODES_MAX`
*   (100), the `PUT` rule above. The member at which the running total first passes 100 is an issue
*   at `['caps', i]`, "the caps of one pool on one cloud total at most 100"
*   (`refineTierCapPoolTotals` of `desired-state.schemas.ts`).
*
* `.strict()`. A conforming answer parses to itself. Pure.
*/
const dpPoolTierCapsResponseSchema = z.object({
	dataPlaneId: storedIdSchema,
	caps: z.array(dpPoolTierCapSchema)
}).strict().superRefine(capIssues);
//#endregion
export { DP_POOL_TIER_CAPS_ADMIN_PATH, DP_POOL_TIER_CAPS_PATH, DP_POOL_TIER_CAPS_READ_PERMISSION, DP_POOL_TIER_CAPS_WRITE_PERMISSION, DP_POOL_TIER_CAP_ADMIN_PATH, DP_POOL_TIER_CAP_PATH, deleteDpPoolTierCapQuerySchema, dpPoolTierCapPathParamsSchema, dpPoolTierCapSchema, dpPoolTierCapsPathParamsSchema, dpPoolTierCapsResponseSchema, putDpPoolTierCapRequestSchema };

//# sourceMappingURL=dp-pool-tier-caps.js.map